import { type Ref, computed, onBeforeUnmount, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useIntersectionObserver, useTemplateRefsList } from '@vueuse/core'

import {
  PRODUCT_COMPARISON_CAMERA_CAT_INFOS,
  PRODUCT_COMPARISON_CAMERA_FIELDS,
  PRODUCT_COMPARISON_DURABILITY_CAT_INFOS,
  PRODUCT_COMPARISON_DURABILITY_FIELDS,
  PRODUCT_COMPARISON_MISCELANEOUS_FIELDS,
  PRODUCT_COMPARISON_MULTIMEDIA_CAT_INFOS,
  PRODUCT_COMPARISON_MULTIMEDIA_FIELDS,
  PRODUCT_COMPARISON_PERFORMANCE_CAT_INFOS,
  PRODUCT_COMPARISON_PERFORMANCE_FIELDS,
  PRODUCT_COMPARISON_SCREEN_QUALITY_CAT_INFOS,
  PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
  PRODUCT_COMPARISON_SOUND_QUALITY_CAT_INFOS,
  PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
} from '../components/ProductComparison/ProductComparison.constants'
import translations from '../components/ProductComparison/ProductComparison.translations'
import type { ComparisonProduct, ProductSpecification } from '../models/product'
import type { Line, TableData } from '../models/productComparison.ts'

export function useProductComparisonModal({
  products,
  productsSpecifications,
}: {
  products: Ref<ComparisonProduct[]>
  productsSpecifications: Ref<ProductSpecification[][]>
}) {
  const blockImpressionTracked = ref<Array<string>>([])
  const sectionRef = useTemplateRefsList<HTMLElement>()
  const i18n = useI18n()
  const { trackComparisonBlockImpression } = useTracking()

  function trackImpression(blockId: string) {
    if (blockImpressionTracked.value.includes(blockId)) return
    blockImpressionTracked.value.push(blockId)
    trackComparisonBlockImpression({
      block_name: blockId,
    })
  }

  function initBlockImpressionTracking() {
    sectionRef.value.forEach((element) => {
      if (!element.id) return
      const rect = element.getBoundingClientRect()
      const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight
      if (isVisible) trackImpression(element.id)
    })
  }

  const categoryId = computed(() => products.value.map((i) => i.categoryId)[0])

  const { stop } = useIntersectionObserver(
    sectionRef,
    ([{ isIntersecting, target }]) => {
      if (isIntersecting && target.id) trackImpression(target.id)
    },
    { threshold: 0.1 },
  )

  function getInfoField(categoryInfos: { [key: number]: string }): Line {
    return {
      type: 'info',
      value:
        categoryId.value && categoryInfos[categoryId.value]
          ? i18n(
              translations[
                categoryInfos[categoryId.value] as keyof typeof translations
              ],
            )
          : '',
    }
  }

  function getScoreField(
    scoreField:
      | 'lifeExpectancy'
      | 'performances'
      | 'camera'
      | 'screenQuality'
      | 'soundQuality'
      | 'multimedia',
  ): Line {
    return {
      type: 'scores',
      values: products.value.map((product) =>
        parseFloat(product.scores?.[scoreField] ?? '0'),
      ),
    }
  }

  function getSpecificationField(field: string): Line {
    let title = ''
    const values = productsSpecifications.value.map(
      (productsSpectification) => {
        const productSpectification = productsSpectification.find(
          (i) => i.field === field,
        )
        if (!productSpectification) return null
        if (productSpectification.display) title = productSpectification.display

        return productSpectification.values.map((i) => i.label).join(', ')
      },
    )

    return {
      type: 'text',
      title,
      values,
    }
  }

  function getSpecificationFields(fields: string[]): Line[] {
    return fields.map((field) => getSpecificationField(field))
  }

  const tableData = computed<TableData[]>(() => {
    const res: TableData[] = [
      {
        sections: [
          {
            lines: [
              {
                type: 'ratings',
                title: i18n(translations.ratings),
                values: products.value.map(
                  (product) => product.reviewRating?.average || 0,
                ),
              },
              {
                type: 'colors',
                title: i18n(translations.colorOptions),
                values: products.value.map(
                  (product) => product.specifications?.color || [],
                ),
              },
              {
                type: 'tags',
                title: i18n(translations.storageOptions),
                values: products.value.map(
                  (product) =>
                    product.specifications?.storage?.map((i) => i.name) || [],
                ),
              },
            ],
          },
        ],
      },
      {
        title: i18n(translations.scores),
        sections: [
          {
            title: i18n(translations.durability),
            trackingKey: 'comparison_tool_durability',
            lines: [
              getScoreField('lifeExpectancy'),
              getInfoField(PRODUCT_COMPARISON_DURABILITY_CAT_INFOS),
              ...PRODUCT_COMPARISON_DURABILITY_FIELDS.map((field) =>
                getSpecificationField(field),
              ),
            ],
          },
          {
            title: i18n(translations.performance),
            trackingKey: 'comparison_tool_performance',
            lines: [
              getScoreField('performances'),
              getInfoField(PRODUCT_COMPARISON_PERFORMANCE_CAT_INFOS),
              ...getSpecificationFields(PRODUCT_COMPARISON_PERFORMANCE_FIELDS),
            ],
          },
          {
            title: i18n(translations.screenQuality),
            trackingKey: 'comparison_tool_screen_quality',
            lines: [
              getScoreField('screenQuality'),
              getInfoField(PRODUCT_COMPARISON_SCREEN_QUALITY_CAT_INFOS),
              ...getSpecificationFields(
                PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.soundQuality),
            trackingKey: 'comparison_tool_sound_quality',
            lines: [
              getScoreField('soundQuality'),
              getInfoField(PRODUCT_COMPARISON_SOUND_QUALITY_CAT_INFOS),
              ...getSpecificationFields(
                PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.multimedia),
            trackingKey: 'comparison_tool_multimedia',
            lines: [
              getScoreField('multimedia'),
              getInfoField(PRODUCT_COMPARISON_MULTIMEDIA_CAT_INFOS),
              ...getSpecificationFields(PRODUCT_COMPARISON_MULTIMEDIA_FIELDS),
            ],
          },
          {
            title: i18n(translations.camera),
            trackingKey: 'comparison_tool_camera',
            lines: [
              getScoreField('camera'),
              getInfoField(PRODUCT_COMPARISON_CAMERA_CAT_INFOS),
              ...getSpecificationFields(PRODUCT_COMPARISON_CAMERA_FIELDS),
            ],
          },
        ],
      },
      {
        title: i18n(translations.miscelaneous),
        separator: true,
        sections: [
          {
            trackingKey: 'comparison_tool_miscelaneous',
            lines: [
              ...getSpecificationFields(PRODUCT_COMPARISON_MISCELANEOUS_FIELDS),
            ],
          },
        ],
      },
    ]

    for (const tableGroup of res) {
      for (const section of tableGroup.sections) {
        // Filter empty lines
        section.lines = section.lines.filter((line) => {
          if (line.type === 'colors' || line.type === 'tags')
            return line.values?.find((v) => v?.length > 0)
          if (line.type === 'ratings' || line.type === 'scores')
            return line.values?.find((v) => v > 0)
          if (line.type === 'info') return !!line.value

          return line.values?.find((v) => !!v)
        })
      }
      // Filter empty sections
      tableGroup.sections = tableGroup.sections.filter(
        (section) => !!section.lines?.length,
      )
    }

    // Filter empty tableGroups
    return res.filter((tableGroup) => !!tableGroup.sections.length)
  })

  onBeforeUnmount(() => {
    stop()
  })

  watch(sectionRef.value, () => {
    initBlockImpressionTracking()
  })

  return {
    blockImpressionTracked,
    sectionRef,
    tableData,
  }
}
